import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import { toast } from "@components/ui/use-toast";
import CustomSelect from "@components/common/CustomSelect";
import { workflowTypes } from "./common/commonData";
import { Trash2, Copy } from "lucide-react";
import { Badge } from "@components/ui/badge";
import InfoTooltip from "@components/common/InfoTooltip";

const THREAT_LOOKUP_OPTIONS = ["domain", "email", "hash", "ip", "url"];

type CreateMeshFlowDialogProps = {
  showDialog: boolean;
  setShowDialog: (value: boolean) => void;
  existingMeshFlows: any[];
  onConfirm: (meshflowData: {
    uid: string;
    title: string;
    description?: string;
    type_workflow: string;
    version: string;
    is_active: boolean;
    cron_run_time?: string | null;
    dependency_workflow?: string[];
    threat_lookup_search_types: string[];
    threatlookup_data?: {
      search_type: string;
      search_value: string;
    };
    workflow_priority?: string;
    is_export?: boolean;
  }) => void;
  editingMeshFlow?: any;
  setEditingMeshFlow?: (meshflow: any | null) => void;
  onDelete?: (uid: string) => void;
};

const CreateMeshFlowDialog: React.FC<CreateMeshFlowDialogProps> = ({
  showDialog,
  setShowDialog,
  existingMeshFlows,
  onConfirm,
  editingMeshFlow,
  setEditingMeshFlow,
  onDelete,
}) => {
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [typeWorkflow, setTypeWorkflow] = React.useState("none");
  const [version, setVersion] = React.useState("1.0");
  const [isActive, setIsActive] = React.useState(true);
  const [cronRunTime, setCronRunTime] = React.useState("");
  const [dependencyWorkflow, setDependencyWorkflow] = React.useState<string[]>(
    [],
  );
  const [threatLookupSearchTypes, setThreatLookupSearchTypes] = React.useState<
    string[]
  >([]);
  const [searchType, setSearchType] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const [searchValueError, setSearchValueError] = React.useState("");
  const [workflowPriority, setWorkflowPriority] = React.useState("");
  const [isExport, setIsExport] = React.useState(false);

  // Validation functions for different search types
  const validateSearchValue = React.useCallback(
    (value: string, type: string): string => {
      if (!value.trim() || !type) return "";

      const validations: Record<string, { regex: RegExp; message: string }> = {
        domain: {
          regex:
            /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
          message: "Invalid domain format (e.g., example.com)",
        },
        email: {
          regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format (e.g., user@example.com)",
        },
        hash: {
          regex: /^[a-fA-F0-9]{32,128}$/,
          message: "Invalid hash format (32-128 hex characters)",
        },
        ip: {
          regex:
            /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
          message: "Invalid IP address format (e.g., 192.168.1.1)",
        },
        url: {
          regex: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
          message: "Invalid URL format (e.g., https://example.com)",
        },
      };

      const validation = validations[type];
      if (validation && !validation.regex.test(value.trim())) {
        return validation.message;
      }

      return "";
    },
    [],
  );

  // Validate search value whenever it or search type changes
  React.useEffect(() => {
    if (searchType && searchValue) {
      const error = validateSearchValue(searchValue, searchType);
      setSearchValueError(error);
    } else {
      setSearchValueError("");
    }
  }, [searchValue, searchType, validateSearchValue]);

  // Extract creation date from UID
  const getCreatedDate = (uid: string): string => {
    if (!uid) return "";

    // UID format: workflow_1777361422060_mryrmds or similar
    const parts = uid.split("_");
    if (parts.length < 2) return "";

    const epochStr = parts[1];
    const epoch = parseInt(epochStr, 10);

    if (isNaN(epoch)) return "";

    const date = new Date(epoch);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${day}-${month}-${year} ${time}`;
  };

  const createdDate = editingMeshFlow
    ? getCreatedDate(editingMeshFlow.uid)
    : "";

  const handleConfirm = () => {
    if (!title.trim()) return;

    if (showCronField && !isExport) {
      const cronValue = Number(cronRunTime?.trim());
      if (!Number.isFinite(cronValue) || cronValue < 15) {
        toast({
          title: "Invalid Cron Frequency",
          description: "Cron Frequency must be at least 15 minutes.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }
    }

    // Skip validation for export flows
    if (!isExport) {
      // Validate threat lookup search types if workflow is threatlookup
      if (
        typeWorkflow === "threatlookup" &&
        threatLookupSearchTypes.length === 0
      ) {
        toast({
          title: "Threat Lookup Search Types Required",
          description:
            "At least one threat lookup search type must be selected for threat lookup workflows.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }
    }

    // Check for duplicate names
    const isDuplicate = existingMeshFlows.some(
      (flow) =>
        flow.title.toLowerCase() === title.trim().toLowerCase() &&
        flow.uid !== editingMeshFlow?.uid,
    );

    if (isDuplicate) {
      toast({
        title: "Duplicate Title",
        description:
          "A mesh flow with this title already exists. Please choose a different title.",
        variant: "destructive",
        duration: 2000,
      });
      return;
    }

    const meshflowData = {
      uid: editingMeshFlow ? editingMeshFlow.uid : `${Date.now()}`,
      title: title.trim(),
      ...(description.trim() && { description: description.trim() }),
      type_workflow: typeWorkflow,
      version: version.trim(),
      is_active: isActive,
      is_export: isExport,
      cron_run_time: showCronField && !isExport ? cronRunTime.trim() : null,
      dependency_workflow: isExport ? [] : dependencyWorkflow,
      threat_lookup_search_types:
        typeWorkflow === "threatlookup" && !isExport
          ? threatLookupSearchTypes
          : [],
      ...(typeWorkflow === "threatlookup" &&
        !isExport &&
        searchType &&
        searchValue.trim() && {
          threatlookup_data: {
            search_type: searchType,
            search_value: searchValue.trim(),
          },
        }),
      ...(showPriorityField &&
        !isExport &&
        workflowPriority && { workflow_priority: workflowPriority }),
    };

    // Call the parent's onConfirm callback
    onConfirm(meshflowData);

    // Don't close the dialog here - let parent handle it based on success/error
  };

  const handleCancel = () => {
    setShowDialog(false);
    // Reset form
    setTitle("");
    setDescription("");
    setTypeWorkflow("none");
    setVersion("1.0");
    setIsActive(true);
    setThreatLookupSearchTypes([]);
    setSearchType("");
    setSearchValue("");
    setSearchValueError("");
    setWorkflowPriority("");
  };

  // Reset all fields when dialog closes or opens
  React.useEffect(() => {
    if (!showDialog) {
      // Clear all fields when dialog closes
      setTitle("");
      setDescription("");
      setTypeWorkflow("none");
      setVersion("1.0");
      setIsActive(true);
      if (setEditingMeshFlow) {
        setEditingMeshFlow(null);
      }
      setCronRunTime("");
      setDependencyWorkflow([]);
      setThreatLookupSearchTypes([]);
      setSearchType("");
      setSearchValue("");
      setSearchValueError("");
      setWorkflowPriority("");
      setIsExport(false);
    }
  }, [showDialog]);

  // Populate fields when editing an existing mesh flow
  React.useEffect(() => {
    if (editingMeshFlow && showDialog) {
      setTitle(editingMeshFlow.title || "");
      setDescription(editingMeshFlow.description || "");
      const workflowType = editingMeshFlow.type_workflow || "none";
      setTypeWorkflow(workflowType);
      setVersion(editingMeshFlow.version || "1.0");
      setIsActive(editingMeshFlow.is_active || false);

      // Set cronRunTime with a default for scheduled workflows
      const needsCron = ![
        "",
        "none",
        "threatlookup",
        "action",
        "settings",
      ].includes(workflowType);
      const cronValue =
        editingMeshFlow.cron_run_time || (needsCron ? "60" : "");
      setCronRunTime(cronValue);

      setDependencyWorkflow(editingMeshFlow.dependency_workflow || []);
      setThreatLookupSearchTypes(
        editingMeshFlow.threat_lookup_search_types || [],
      );
      setSearchType(editingMeshFlow.threatlookup_data?.search_type || "");
      setSearchValue(editingMeshFlow.threatlookup_data?.search_value || "");
      setWorkflowPriority(editingMeshFlow.workflow_priority || "");
      setIsExport(editingMeshFlow.is_export || false);
    }
  }, [editingMeshFlow, showDialog]);

  // Auto-set cron time when workflow type changes and requires it
  React.useEffect(() => {
    // Skip auto-setting cron time for export flows
    if (isExport) return;

    const needsCron = ![
      "",
      "none",
      "threatlookup",
      "action",
      "settings",
    ].includes(typeWorkflow);
    if (needsCron) {
      setCronRunTime((prev) => {
        // Only set default if currently empty
        if (!prev || prev.trim() === "") {
          return "60"; // Default 60 minutes
        }
        return prev;
      });
    }
  }, [typeWorkflow, isExport]);

  const showCronField = ![
    "",
    "none",
    "threatlookup",
    "action",
    "settings",
  ].includes(typeWorkflow);

  const showPriorityField = ![
    "",
    "none",
    "threatlookup",
    "action",
    "settings",
  ].includes(typeWorkflow);

  // Filter workflowTypes to remove threat_lookup if one already exists
  const filteredWorkflowTypes = React.useMemo(() => {
    const sortWithNoneFirst = (types: typeof workflowTypes) => {
      const noneOption = types.find((wt) => wt.value === "none");
      const otherOptions = types
        .filter((wt) => wt.value !== "none")
        .sort((a, b) => a.label.localeCompare(b.label));

      return noneOption ? [noneOption, ...otherOptions] : otherOptions;
    };

    // Check if a threat lookup workflow already exists (not counting the current editing one)
    const hasThreatLookup = existingMeshFlows.some(
      (flow) =>
        (flow.type_workflow === "threatlookup" ||
          flow.type_workflow === "threat_lookup") &&
        flow.uid !== editingMeshFlow?.uid,
    );

    // If threat lookup exists, filter it out from the options
    if (hasThreatLookup) {
      const filteredTypes = workflowTypes.filter(
        (wt) => wt.value !== "threatlookup" && wt.value !== "threat_lookup",
      );
      return sortWithNoneFirst(filteredTypes);
    }

    return sortWithNoneFirst([...workflowTypes]);
  }, [existingMeshFlows, editingMeshFlow]);

  // Copy to clipboard handler
  const handleCopy = (fieldName: string) => {
    const value =
      fieldName === "search_type"
        ? `{{$threatlookup.search_type}}`
        : `{{$threatlookup.search_value}}`;
    navigator.clipboard.writeText(value);
    toast({
      variant: "info",
      title: "Copied",
      description: `${fieldName} copied to clipboard`,
      duration: 2000,
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent disableOutsideClick className="!max-w-[50vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <div>
                {editingMeshFlow
                  ? "MeshFlow : " + editingMeshFlow.title
                  : "Create New Mesh Flow"}{" "}
                <Badge
                  className={`${isActive ? "bg-primaryGreen" : "bg-primary"}`}
                >
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              {editingMeshFlow && createdDate && (
                <div className="font-normal text-sm font-mono text-muted-foreground">
                  Created On: {createdDate}
                </div>
              )}
            </div>
            <div
              className={`flex items-center justify-between px-3 py-2 rounded-md mr-6 border ${isActive ? "border-primaryGreen bg-lightGreen" : "bg-lightRed border border-primary"}`}
            >
              <div className="flex items-center gap-3">
                <Label
                  htmlFor="meshflow-active"
                  className={`${isActive ? "text-primaryGreen" : "text-primary"}`}
                >
                  {isActive ? "Deactivate" : "Activate"} Meshflow
                </Label>
                <Switch
                  id="meshflow-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  className="data-[state=checked]:bg-primaryGreen"
                />
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Meshflow for Export Checkbox */}
        {/* <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-muted bg-muted/30">
          <Switch
            id="meshflow-export"
            checked={isExport}
            onCheckedChange={setIsExport}
            className="data-[state=checked]:bg-primaryBlue"
          />
          <Label
            htmlFor="meshflow-export"
            className={`cursor-pointer ${isExport ? "text-primaryBlue font-semibold" : "text-foreground"}`}
          >
            Meshflow for Export?
          </Label>
        </div> */}

        <div className={`grid grid-cols-2 gap-4 mt-1 border p-4 rounded-md`}>
          <div>
            <Label htmlFor="meshflow-title">
              Workflow Title <span className="text-primary">*</span>
            </Label>
            <Input
              id="meshflow-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter mesh flow title"
              autoComplete="off"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="meshflow-description">Description</Label>
            <textarea
              id="meshflow-description"
              className="w-full p-2 border rounded-md text-sm"
              rows={1}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter mesh flow description"
            />
          </div>

          <div>
            <Label htmlFor="meshflow-type">
              Type of Workflow <span className="text-primary">*</span>
            </Label>
            <CustomSelect
              data={filteredWorkflowTypes}
              defaultValue={typeWorkflow}
              onSelect={(value: string) => setTypeWorkflow(value)}
              placeholder="Select workflow type"
              multiSelect={false}
            />
          </div>
          <div>
            <Label htmlFor="meshflow-version">
              Version <span className="text-primary">*</span>
            </Label>
            <Input
              id="meshflow-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.0"
              autoComplete="off"
            />
          </div>

          {/* Only show other fields when NOT for export */}
          {!isExport && (
            <>
              {typeWorkflow === "threatlookup" && (
                <>
                  <div>
                    <Label htmlFor="meshflow-threat-lookup">
                      Threat Lookup Search Types{" "}
                      <span className="text-primary">*</span>
                    </Label>
                    <CustomSelect
                      key={`threat-lookup-${editingMeshFlow?.uid || "new"}`}
                      data={THREAT_LOOKUP_OPTIONS.map((type) => ({
                        label: type.charAt(0).toUpperCase() + type.slice(1),
                        value: type,
                      }))}
                      defaultValue={threatLookupSearchTypes}
                      onSelect={(value: string[]) =>
                        setThreatLookupSearchTypes(value)
                      }
                      placeholder="Select threat lookup types"
                      multiSelect={true}
                    />
                  </div>
                  {threatLookupSearchTypes.length > 0 && (
                    <>
                      <div>
                        <Label htmlFor="meshflow-search-type">
                          Search Type
                        </Label>
                        <div className="flex items-center gap-2">
                          <CustomSelect
                            key={`search-type-${editingMeshFlow?.uid || "new"}-${threatLookupSearchTypes.join(",")}`}
                            data={threatLookupSearchTypes.map((type) => ({
                              label:
                                type.charAt(0).toUpperCase() + type.slice(1),
                              value: type,
                            }))}
                            defaultValue={searchType}
                            onSelect={(value: string) => setSearchType(value)}
                            placeholder="Select search type"
                            multiSelect={false}
                            disabled={threatLookupSearchTypes.length === 0}
                            className="w-full"
                          />
                          {searchType && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="shrink-0 w-10"
                              onClick={() => handleCopy("search_type")}
                            >
                              <Copy className="text-primaryBlue" size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="meshflow-search-value">
                          Search Value
                        </Label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Input
                              id="meshflow-search-value"
                              value={searchValue}
                              onChange={(e) => setSearchValue(e.target.value)}
                              placeholder="Enter search value"
                              autoComplete="off"
                              className={
                                searchValueError
                                  ? "border-primary outline-primary"
                                  : ""
                              }
                            />
                            {searchValueError && (
                              <p className="text-primary text-xs mt-1">
                                {searchValueError}
                              </p>
                            )}
                          </div>
                          {searchValue && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="shrink-0 w-10"
                              onClick={() => handleCopy("search_value")}
                            >
                              <Copy className="text-primaryBlue" size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {showCronField && (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <Label htmlFor="meshflow-cron">
                      Cron Frequency in minutes
                      <span className="text-primary"> *</span>
                    </Label>
                    <InfoTooltip text="Cron frequency in minutes (minimum 15 minutes)" />
                  </div>
                  <Input
                    id="meshflow-cron"
                    value={cronRunTime}
                    onChange={(e) => setCronRunTime(e.target.value)}
                    placeholder="eg : 15 for (every 15 minutes) "
                  />
                </div>
              )}
              {showPriorityField && (
                <div>
                  <Label htmlFor="meshflow-priority">
                    Workflow Priority<span className="text-primary">*</span>
                  </Label>
                  <CustomSelect
                    data={[
                      { label: "Low", value: "low" },
                      { label: "Medium", value: "medium" },
                      { label: "High", value: "high" },
                    ]}
                    defaultValue={workflowPriority}
                    onSelect={(value: string) => setWorkflowPriority(value)}
                    placeholder="Select priority"
                    multiSelect={false}
                  />
                </div>
              )}
              {existingMeshFlows.length > 0 && (
                <div>
                  <Label htmlFor="meshflow-dependency">
                    Dependent Workflows
                  </Label>
                  <CustomSelect
                    data={existingMeshFlows
                      .filter((flow) => flow.uid !== editingMeshFlow?.uid)
                      .map((flow) => ({
                        label: flow.title,
                        value: flow.uid,
                      }))}
                    defaultValue={dependencyWorkflow}
                    onSelect={(value: string[]) => setDependencyWorkflow(value)}
                    placeholder="Select dependent workflows"
                    multiSelect={true}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          {editingMeshFlow && onDelete ? (
            <Button
              onClick={() => {
                onDelete(editingMeshFlow.uid);
                setShowDialog(false);
              }}
            >
              <Trash2 size={16} className="mr-2" />
              Delete Mesh flow
            </Button>
          ) : (
            <div></div>
          )}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={
                !title.trim() ||
                typeWorkflow === "none" ||
                typeWorkflow == "" ||
                typeWorkflow == null ||
                !version.trim() ||
                (!isExport &&
                  ((showCronField &&
                    (!cronRunTime || cronRunTime.trim() === "")) ||
                    (typeWorkflow === "threatlookup" &&
                      (threatLookupSearchTypes.length === 0 ||
                        !!searchValueError)) ||
                    (showPriorityField && !workflowPriority)))
              }
              className="bg-transparent text-primary border border-primary hover:bg-primary hover:text-white"
            >
              {editingMeshFlow ? "Update Mesh Flow" : "Create Mesh Flow"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMeshFlowDialog;
